import React from 'react';
import { Button, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import useMessage from 'src/app/hooks/useMessage';
import handleError from '@fuse/core/errorHandler';
import {
  useCreateProjectIndustryMutation,
  useUpdateProjectIndustryMutation,
} from 'src/app/services/projectInfomationApi';

const defaultValues = {
  nameUz: '',
  nameRu: '',
};

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const ProjectIndustriesForm = ({ tempCat, isEdit, onClose }) => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();

  const [updateCategory, { isLoading: isUpdateLoading }] = useUpdateProjectIndustryMutation();
  const [createCategory, { isLoading: isCreateLoading }] = useCreateProjectIndustryMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: tempCat ?? defaultValues,
  });

  React.useEffect(() => {
    if (tempCat) {
      reset({
        nameUz: tempCat.nameUz || '',
        nameRu: tempCat.nameRu || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [tempCat, reset]);

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
  };

  const trimObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const makeRules = () => ({
    required: t('EmptyInput') || true,
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('EmptyInput') || 'This field cannot be empty';
      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    fieldOnChange(e.target.value.trimStart());
  };

  const onSubmit = async (data) => {
    const payload = trimObject(data);
    const message = isEdit ? t('SuccessfullyUpdated') : t('SuccessfullyAdded');

    const promise = isEdit
      ? updateCategory({ id: tempCat?.id, ...payload })
      : createCategory(payload);

    promise
      .unwrap()
      .then(() => {
        showMessage({ message, variant: 'success' });
        handleClose();
      })
      .catch((err) => handleError({ response: err }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16 pt-10 max-w-7xl">
      <Controller
        name="nameUz"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={`${t('Title')} (uzb.)`}
            error={!!errors.nameUz}
            helperText={errors.nameUz?.message}
            variant="outlined"
            required
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />

      <Controller
        name="nameRu"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={`${t('Title')} (рус.)`}
            error={!!errors.nameRu}
            helperText={errors.nameRu?.message}
            variant="outlined"
            required
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />

      <Button
        disabled={isUpdateLoading || isCreateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default ProjectIndustriesForm;
